import React from 'react';
import { ActivityIndicator, Pressable, type PressableProps } from 'react-native';

import { palette } from '@/config/theme';

import { AppText } from './AppText';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

// Calm glass-green primary (translucent fill + green edge + bright-green label)
// instead of a loud acid block, so it reads clearly without straining the eye.
const CONTAINER_CLASSES: Record<ButtonVariant, string> = {
  primary: 'bg-volt-glass border border-volt-line',
  secondary: 'bg-glass-2 border border-glass-border',
  ghost: 'bg-transparent',
  danger: 'bg-loss/10 border border-loss/40',
};

const LABEL_CLASSES: Record<ButtonVariant, string> = {
  primary: 'text-volt-bright',
  secondary: 'text-ink',
  ghost: 'text-volt',
  danger: 'text-loss',
};

const SPINNER_COLORS: Record<ButtonVariant, string> = {
  primary: palette.voltBright,
  secondary: palette.text,
  ghost: palette.volt,
  danger: palette.loss,
};

export interface ButtonProps extends Omit<PressableProps, 'children'> {
  label: string;
  variant?: ButtonVariant;
  loading?: boolean;
  compact?: boolean;
  className?: string;
}

export function Button({
  label,
  variant = 'primary',
  loading = false,
  compact = false,
  disabled,
  className,
  ...rest
}: ButtonProps) {
  const isDisabled = disabled || loading;
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{ disabled: isDisabled, busy: loading }}
      disabled={isDisabled}
      className={`${CONTAINER_CLASSES[variant]} ${
        compact ? 'px-4 py-2.5 rounded-card' : 'px-6 py-3.5 rounded-card'
      } flex-row items-center justify-center gap-2 active:opacity-80 ${
        isDisabled ? 'opacity-50' : ''
      }${className ? ` ${className}` : ''}`}
      {...rest}
    >
      {loading ? <ActivityIndicator size="small" color={SPINNER_COLORS[variant]} /> : null}
      <AppText
        variant={compact ? 'caption' : 'body'}
        weight="semibold"
        className={LABEL_CLASSES[variant]}
      >
        {label}
      </AppText>
    </Pressable>
  );
}
